import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

export function ContactForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        },
      });

      if (error) {
        const payload = error.context as { body?: string } | undefined;
        let msg = error.message;
        if (payload?.body) {
          try {
            const parsed = JSON.parse(payload.body) as { error?: string };
            if (parsed?.error) msg = parsed.error;
          } catch {
            /* use error.message */
          }
        }
        throw new Error(msg);
      }

      const errBody = data && typeof data === 'object' && 'error' in data ? (data as { error?: string }).error : undefined;
      if (errBody) {
        throw new Error(errBody);
      }

      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (err: unknown) {
      console.error('Contact form error:', err);
      setStatus('error');
      const message = err instanceof Error ? err.message : t('contact.error');
      setErrorMsg(message || t('contact.error'));
    }
  };

  return (
    <div className="bg-[#101828] py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-extrabold text-white mb-4">{t('contact.title')}</h2>
          <p className="text-xl text-gray-300">{t('contact.subtitle')}</p>
        </div>

        {status === 'success' ? (
          <div className="flex flex-col items-center text-center py-12">
            <CheckCircle className="h-16 w-16 text-[#3ECF8E] mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-2">{t('contact.successTitle')}</h3>
            <p className="text-gray-300">{t('contact.successMessage')}</p>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="mt-6 text-[#8B5CF6] hover:underline text-sm"
            >
              {t('contact.sendAnother')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('contact.name')}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t('contact.namePlaceholder')}
                  className="w-full bg-[#1a2535] border border-gray-600 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#8B5CF6] transition-colors"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('contact.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t('contact.emailPlaceholder')}
                  className="w-full bg-[#1a2535] border border-gray-600 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#8B5CF6] transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                {t('contact.message')}
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={handleChange}
                placeholder={t('contact.messagePlaceholder')}
                className="w-full bg-[#1a2535] border border-gray-600 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#8B5CF6] transition-colors resize-none"
              />
            </div>

            {status === 'error' && (
              <p className="text-red-400 text-sm">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#8B5CF6] hover:bg-[#9D72F7] disabled:opacity-60 disabled:cursor-not-allowed text-white text-lg font-medium rounded-lg transition-colors"
            >
              {status === 'loading' ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  {t('contact.sending')}
                </>
              ) : (
                <>
                  {t('contact.send')}
                  <Send className="ml-2 h-5 w-5" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}